import React, { useState, useEffect, useRef } from "react";

const AmountDropdown = ({
  currencies,
  onCurrencyChange,
  onAmountChange,
  initialCurrency = "GBP",
  initialAmount = "",
  value,
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [amount, setAmount] = useState(initialAmount || value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (initialCurrency) setSelectedCurrency(initialCurrency);
  }, [initialCurrency]);

  useEffect(() => {
    if (value !== undefined) setAmount(value);
    else if (initialAmount !== undefined) setAmount(initialAmount);
  }, [value, initialAmount]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCurrencyChange = (code) => {
    setSelectedCurrency(code);
    setIsOpen(false);
    onCurrencyChange(code);
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setAmount(val);
      onAmountChange(val);
    }
  };

  const getCurrencySymbol = (code) => {
    const c = currencies.find((c) => c.code === code);
    return c ? c.symbol : "£";
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');

        .ad-wrap {
          position: relative; width: 100%;
          font-family: 'DM Sans', sans-serif;
        }

        .ad-field {
          display: flex; align-items: center;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          transition: border-color 0.15s, box-shadow 0.15s;
          overflow: hidden;
        }

        .ad-field.focused {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }

        .ad-symbol {
          padding: 9px 0 9px 14px;
          font-family: 'DM Mono', monospace;
          font-size: 13px; font-weight: 500; color: #64748B;
          flex-shrink: 0;
        }

        .ad-input {
          flex: 1; padding: 9px 8px;
          font-family: 'DM Mono', monospace;
          font-size: 13px; color: #0F172A;
          border: none; outline: none; background: transparent;
          -moz-appearance: textfield;
        }
        .ad-input::-webkit-outer-spin-button,
        .ad-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ad-input::placeholder { color: #94A3B8; font-family: 'DM Sans', sans-serif; }

        .ad-divider {
          width: 1px; height: 20px; background: #E2E8F0; flex-shrink: 0;
        }

        .ad-currency-btn {
          display: flex; align-items: center; gap: 5px;
          padding: 9px 12px; cursor: pointer;
          font-size: 12px; font-weight: 700; color: #475569;
          background: #F8FAFC; border: none; outline: none;
          transition: background 0.15s, color 0.15s;
          flex-shrink: 0; white-space: nowrap;
        }
        .ad-currency-btn:hover { background: #F1F5F9; color: #0F172A; }

        .ad-chevron {
          transition: transform 0.15s;
          color: #94A3B8;
        }
        .ad-chevron.open { transform: rotate(180deg); }

        .ad-dropdown {
          position: absolute; top: calc(100% + 6px); left: 0; right: 0;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
          max-height: 200px; overflow-y: auto; z-index: 50;
          padding: 4px;
        }

        .ad-option {
          display: flex; align-items: center; gap: 8px;
          padding: 9px 12px; border-radius: 7px; cursor: pointer;
          transition: background 0.12s;
        }
        .ad-option:hover { background: #F1F5F9; }
        .ad-option.selected { background: #EFF6FF; }

        .ad-option-symbol {
          font-family: 'DM Mono', monospace;
          font-size: 13px; font-weight: 500; color: #2563EB;
          width: 18px; flex-shrink: 0; text-align: center;
        }

        .ad-option-code {
          font-size: 13px; font-weight: 700; color: #0F172A;
        }

        .ad-option-name {
          font-size: 11px; color: #94A3B8; flex: 1;
        }

        .ad-option-check {
          margin-left: auto; color: #2563EB; flex-shrink: 0;
        }
      `}</style>

      <div className="ad-wrap" ref={dropdownRef}>
        <div className={`ad-field${isFocused ? " focused" : ""}`}>

          {/* Currency symbol */}
          <span className="ad-symbol">{getCurrencySymbol(selectedCurrency)}</span>

          {/* Amount input */}
          <input
            className="ad-input"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />

          <div className="ad-divider" />

          {/* Currency selector button */}
          <button
            className="ad-currency-btn"
            type="button"
            onClick={() => setIsOpen((o) => !o)}
          >
            {selectedCurrency}
            <svg
              className={`ad-chevron${isOpen ? " open" : ""}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none"
            >
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="ad-dropdown">
            {currencies.map((c) => (
              <div
                key={c.code}
                className={`ad-option${c.code === selectedCurrency ? " selected" : ""}`}
                onClick={() => handleCurrencyChange(c.code)}
              >
                <span className="ad-option-symbol">{c.symbol}</span>
                <span className="ad-option-code">{c.code}</span>
                <span className="ad-option-name">{c.name}</span>
                {c.code === selectedCurrency && (
                  <svg className="ad-option-check" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AmountDropdown;